import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  FileText,
  Flag,
  Loader2,
  MessageSquare,
  Sparkles,
  TimerReset,
  Trophy,
  UserCheck,
  Users,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

const formatDateTime = (value) => {
  if (!value) return 'Not yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

const getUniqueMemberIds = (items = []) => (
  Array.from(new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => item?.user_id)
      .filter(Boolean),
  ))
);

const AnalyticsTab = ({
  timeRange,
  setTimeRange,
  analytics,
  atRiskMembers,
  onboardingAnalytics,
  weeklyRhythmAnalytics,
  loading,
  activeZone,
  onOpenTool,
  adminFetch,
}) => {
  const [showProgressDebug, setShowProgressDebug] = useState(false);
  const [progressDebug, setProgressDebug] = useState(null);
  const [progressDebugLoading, setProgressDebugLoading] = useState(false);
  const [progressDebugError, setProgressDebugError] = useState('');

  useEffect(() => {
    if (!showProgressDebug || progressDebug || progressDebugLoading || !adminFetch) return;

    let cancelled = false;

    const loadProgressDebug = async () => {
      try {
        setProgressDebugLoading(true);
        setProgressDebugError('');
        const data = await adminFetch('/admin/member-progress/debug?limit=10');
        if (!cancelled) {
          setProgressDebug(data);
        }
      } catch (error) {
        if (!cancelled) {
          setProgressDebugError(error.message || 'Failed to load operator debug data');
        }
      } finally {
        if (!cancelled) {
          setProgressDebugLoading(false);
        }
      }
    };

    loadProgressDebug();

    return () => {
      cancelled = true;
    };
  }, [adminFetch, progressDebug, progressDebugLoading, showProgressDebug]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-aqua animate-spin" />
      </div>
    );
  }

  const isOperationsZone = activeZone?.id === 'stewardship';
  const isDadPartnersZone = activeZone?.id === 'heart-work';
  const title = isOperationsZone
    ? 'Business Health Analytics'
    : isDadPartnersZone
      ? 'Feedback & Insight Analytics'
      : 'Community Analytics';
  const helperText = isOperationsZone
    ? 'Retention, growth, and stability signals that show whether the business is staying durable enough to support the mission.'
    : isDadPartnersZone
      ? 'Signals that help you see how dads are engaging and where the deeper feedback patterns may be pointing.'
      : 'Core analytics for the current operating lane.';

  const kpis = [
    {
      label: 'Engagement Rate',
      value: `${analytics?.engagement?.engagement_rate || 0}%`,
      description: 'Active members this period',
      icon: Activity,
      importance: 'critical'
    },
    {
      label: 'Retention Rate',
      value: `${analytics?.retention?.retention_rate || 0}%`,
      description: 'Returning members',
      icon: UserCheck,
      importance: 'critical'
    },
    {
      label: 'Posts/Member',
      value: analytics?.engagement?.posts_per_member || 0,
      description: 'Average posts per member',
      icon: FileText,
      importance: 'high'
    },
    {
      label: 'Comments/Post',
      value: analytics?.engagement?.comments_per_post || 0,
      description: 'Average comments per post',
      icon: MessageSquare,
      importance: 'high'
    },
  ];

  const engagementMetrics = [
    { label: 'Active Members', value: analytics?.overview?.active_members || 0 },
    { label: 'New Members', value: analytics?.overview?.new_members || 0 },
    { label: 'Posts This Period', value: analytics?.engagement?.posts_in_period || 0 },
    { label: 'Comments This Period', value: analytics?.engagement?.comments_in_period || 0 },
    { label: 'Total Recordings', value: analytics?.overview?.total_recordings || 0 },
  ];

  const onboardingSummary = onboardingAnalytics?.summary || {};
  const onboardingCards = [
    { label: 'Eligible dads', value: onboardingSummary.eligible_members || 0 },
    { label: 'Not started', value: onboardingSummary.not_started || 0 },
    { label: 'In progress', value: onboardingSummary.in_progress || 0 },
    { label: 'Completed', value: onboardingSummary.completed || 0 },
    { label: 'Stalled', value: onboardingSummary.stalled || 0 },
  ];
  const weeklyRhythmSummary = weeklyRhythmAnalytics?.summary || {};
  const weeklyRhythmParticipants = weeklyRhythmAnalytics?.participants || {};
  const focusViewerIds = getUniqueMemberIds(weeklyRhythmParticipants.focus_prompt_viewed);
  const winCreatorIds = getUniqueMemberIds(weeklyRhythmParticipants.win_created);
  const winCommenterIds = getUniqueMemberIds(weeklyRhythmParticipants.win_comment_created);
  const engagedDadIds = getUniqueMemberIds([
    ...(weeklyRhythmParticipants.focus_prompt_viewed || []),
    ...(weeklyRhythmParticipants.win_created || []),
    ...(weeklyRhythmParticipants.win_comment_created || []),
  ]);
  const stalledDadIds = getUniqueMemberIds(onboardingAnalytics?.stalled_members);
  const notStartedDadIds = getUniqueMemberIds(onboardingAnalytics?.not_started_members);
  const onboardingAttentionDadIds = getUniqueMemberIds([
    ...(onboardingAnalytics?.stalled_members || []),
    ...(onboardingAnalytics?.not_started_members || []),
  ]);
  const hasEngagedDadIds = engagedDadIds.length > 0;
  const hasWinCreatorIds = winCreatorIds.length > 0;
  const hasWinCommenterIds = winCommenterIds.length > 0;
  const hasStalledDadIds = stalledDadIds.length > 0;
  const hasNotStartedDadIds = notStartedDadIds.length > 0;
  const hasOnboardingAttentionDadIds = onboardingAttentionDadIds.length > 0;
  const weeklyRhythmCards = [
    { label: 'Focus viewers', value: weeklyRhythmSummary.focus_prompt_viewers || 0, icon: Sparkles },
    { label: 'Win creators', value: weeklyRhythmSummary.wins_creators || 0, icon: Trophy },
    { label: 'Win commenters', value: weeklyRhythmSummary.wins_commenters || 0, icon: MessageSquare },
    { label: 'Engaged dads', value: weeklyRhythmSummary.engaged_members || 0, icon: Users },
  ];

  const rhythmColumns = [
    {
      key: 'focus_prompt_viewed',
      title: 'Monday Focus Viewed',
      emptyText: 'No dads have viewed Monday focus in this period.',
      ctaLabel: 'Open Prompt Assistant',
      onOpen: () => onOpenTool?.('prompts', 'support-dads'),
    },
    {
      key: 'win_created',
      title: 'Thursday Wins Posted',
      emptyText: 'No wins have been posted in this period.',
      ctaLabel: 'Open Dad Partners',
      onOpen: () => onOpenTool?.('members', 'support-dads', {
        targetMemberIds: winCreatorIds,
        targetScopeLabel: 'Thursday Wins Posted',
      }),
      disabled: !hasWinCreatorIds,
    },
    {
      key: 'win_comment_created',
      title: 'Thursday Win Comments',
      emptyText: 'No win comments have been added in this period.',
      ctaLabel: 'Open Dad Partners',
      onOpen: () => onOpenTool?.('members', 'support-dads', {
        targetMemberIds: winCommenterIds,
        targetScopeLabel: 'Thursday Win Comments',
      }),
      disabled: !hasWinCommenterIds,
    },
  ];

  const formatDebugDateTime = (value) => {
    if (!value) return 'Not yet';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';
    return parsed.toLocaleString();
  };

  const formatDebugLabel = (value) => {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <p className="mt-1 text-sm text-gray-400">{helperText}</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-[#1e1e24] border border-[#2a2a32] text-white rounded-lg px-4 py-2"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="1y">Last Year</option>
        </select>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, idx) => (
          <Card key={idx} className={`bg-[#1e1e24] border-[#2a2a32] p-5 ${kpi.importance === 'critical' ? 'ring-1 ring-aqua/30' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <kpi.icon className="w-6 h-6 text-aqua" />
              {kpi.importance === 'critical' && (
                <Badge className="bg-aqua/10 text-aqua border-aqua/30 text-xs">Key Metric</Badge>
              )}
            </div>
            <p className="text-gray-400 text-sm">{kpi.label}</p>
            <span className="text-2xl font-bold text-white">{kpi.value}</span>
            <p className="text-gray-500 text-xs mt-2">{kpi.description}</p>
          </Card>
        ))}
      </div>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-aqua" />
          Engagement Metrics
        </h3>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
          {engagementMetrics.map((metric, idx) => (
            <div key={idx} className="p-4 bg-[#16161a] rounded-xl">
              <p className="text-gray-400 text-sm">{metric.label}</p>
              <span className="text-xl font-bold text-white">{metric.value}</span>
            </div>
          ))}
        </div>
      </Card>

      {isDadPartnersZone && onboardingAnalytics ? (
        <>
          {weeklyRhythmAnalytics ? (
            <>
              <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-aqua" />
                      Weekly Rhythm Engagement
                    </h3>
                    <p className="text-sm text-gray-400">
                      Dad Partners engagement for Monday focus and Thursday wins during the selected analytics range.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenTool?.('prompts', 'support-dads')}
                      className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      Open Prompt Assistant
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenTool?.('members', 'support-dads', {
                        targetMemberIds: engagedDadIds,
                        targetScopeLabel: 'Weekly Rhythm Engagement',
                      })}
                      disabled={!hasEngagedDadIds}
                      className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      Open Dad Partners
                    </Button>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {weeklyRhythmCards.map((card) => (
                    <div key={card.label} className="rounded-2xl bg-[#16161a] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm text-gray-400">{card.label}</p>
                          <p className="mt-2 text-2xl font-bold text-white">{card.value}</p>
                        </div>
                        <card.icon className="h-5 w-5 text-aqua" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="grid gap-6 xl:grid-cols-3">
                {rhythmColumns.map((column) => {
                  const items = weeklyRhythmAnalytics?.participants?.[column.key] || [];
                  return (
                    <Card key={column.key} className="bg-[#1e1e24] border-[#2a2a32] p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{column.title}</h3>
                          <p className="text-sm text-gray-400">
                            Recent dads counted in this weekly rhythm action.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={column.onOpen}
                          disabled={column.disabled}
                          className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                        >
                          {column.ctaLabel}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                      <div className="mt-4 space-y-3">
                        {items.length ? (
                          items.map((member) => (
                            <div key={`${column.key}:${member.user_id}`} className="rounded-2xl bg-[#16161a] p-4">
                              <p className="font-medium text-white">{member.name}</p>
                              <p className="text-sm text-gray-500">{member.email || member.user_id}</p>
                              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                                <span>When: {formatDateTime(member.occurred_at)}</span>
                                <span>Tier: {member.tier || 'unknown'}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-400">{column.emptyText}</p>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : null}

          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-aqua" />
                  Start Here Onboarding Funnel
                </h3>
                <p className="text-sm text-gray-400">
                  Which current dads have not started, are moving, or have stalled in Start Here. Stalled means no progress for {onboardingSummary.stalled_after_days || 7} days.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenTool?.('members', 'support-dads', {
                  targetMemberIds: onboardingAttentionDadIds,
                  targetScopeLabel: 'Start Here Attention',
                })}
                disabled={!hasOnboardingAttentionDadIds}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                <Users className="mr-2 h-4 w-4" />
                Open Dad Partners
              </Button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              {onboardingCards.map((card) => (
                <div key={card.label} className="rounded-2xl bg-[#16161a] p-4">
                  <p className="text-sm text-gray-400">{card.label}</p>
                  <p className="mt-2 text-2xl font-bold text-white">{card.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-400">Step completion</h4>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {(onboardingAnalytics.step_breakdown || []).map((step) => (
                  <div key={step.key} className="rounded-2xl border border-[#2a2a32] bg-[#16161a] p-4">
                    <p className="text-sm font-medium text-white">{step.title}</p>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-gray-400">Completed</span>
                      <span className="font-semibold text-white">{step.completed_count || 0}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-sm">
                      <span className="text-gray-400">Stalled here</span>
                      <span className="font-semibold text-amber-300">{step.stalled_count || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <TimerReset className="w-5 h-5 text-amber-300" />
                    Stalled Dads
                  </h3>
                  <p className="text-sm text-gray-400">
                    Dads who started Start Here but have not finished and have gone quiet long enough to matter.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenTool?.('members', 'support-dads', {
                    targetMemberIds: stalledDadIds,
                    targetScopeLabel: 'Stalled Dads',
                  })}
                  disabled={!hasStalledDadIds}
                  className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                >
                  {hasStalledDadIds ? 'Open Dad Partners' : 'No stalled dads'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {(onboardingAnalytics.stalled_members || []).length ? (
                  onboardingAnalytics.stalled_members.map((member) => (
                    <div key={member.user_id} className="rounded-2xl bg-[#16161a] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.email || member.user_id}</p>
                        </div>
                        <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30">
                          {member.next_step_title || 'Needs next step'}
                        </Badge>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>Completed steps: {member.completed_steps_count || 0}</span>
                        <span>Last progress: {formatDateTime(member.last_progress_at)}</span>
                        <span>
                          {member.days_since_progress == null ? 'No recent timestamp' : `${member.days_since_progress} days since progress`}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400">No stalled dads right now.</p>
                )}
              </div>
            </Card>

            <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-300" />
                    Not Started Yet
                  </h3>
                  <p className="text-sm text-gray-400">
                    Current dads who still have no saved Start Here progress.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenTool?.('members', 'support-dads', {
                    targetMemberIds: notStartedDadIds,
                    targetScopeLabel: 'Not Started Yet',
                  })}
                  disabled={!hasNotStartedDadIds}
                  className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                >
                  Open Dad Partners
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {(onboardingAnalytics.not_started_members || []).length ? (
                  onboardingAnalytics.not_started_members.map((member) => (
                    <div key={member.user_id} className="rounded-2xl bg-[#16161a] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.email || member.user_id}</p>
                        </div>
                        <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30">
                          {member.next_step_title || 'Start Here'}
                        </Badge>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>Joined: {formatDateTime(member.joined_at)}</span>
                        <span>Status: {member.status || 'current'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400">Everyone in this cohort has at least started.</p>
                )}
              </div>
            </Card>
          </div>

          <section className="rounded-3xl border border-[#2a2a32] bg-[#121217] shadow-[0_24px_80px_-48px_rgba(0,0,0,0.7)]">
            <button
              type="button"
              onClick={() => setShowProgressDebug((value) => !value)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-aqua/70">Operator Debug</p>
                <h3 className="mt-1 text-sm font-semibold text-white">Dad Partners / Prospects data check</h3>
              </div>
              <span className="flex items-center gap-2 text-sm text-gray-300">
                {progressDebugLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {showProgressDebug ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </span>
            </button>

            {showProgressDebug ? (
              <div className="border-t border-[#23232b] px-5 py-5">
                {progressDebugError ? (
                  <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                    {progressDebugError}
                  </div>
                ) : null}

                {progressDebugLoading && !progressDebug ? (
                  <div className="flex items-center gap-3 py-6 text-sm text-gray-300">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading operator debug data...
                  </div>
                ) : null}

                {progressDebug ? (
                  <div className="space-y-5">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">People with progress</p>
                        <p className="mt-2 text-2xl font-semibold text-white">{progressDebug.summary?.members_with_progress ?? 0}</p>
                      </div>
                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Started onboarding</p>
                        <p className="mt-2 text-2xl font-semibold text-white">{progressDebug.summary?.members_started_onboarding ?? 0}</p>
                      </div>
                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Completed onboarding</p>
                        <p className="mt-2 text-2xl font-semibold text-white">{progressDebug.summary?.members_completed_onboarding ?? 0}</p>
                      </div>
                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Tracked events</p>
                        <p className="mt-2 text-2xl font-semibold text-white">{progressDebug.summary?.recent_event_count ?? 0}</p>
                      </div>
                    </div>

                    <div className="grid gap-5 xl:grid-cols-2">
                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] p-4">
                        <div>
                          <h4 className="text-sm font-semibold text-white">Recent progress updates</h4>
                          <p className="mt-1 text-xs text-gray-400">Latest checklist persistence activity.</p>
                        </div>

                        <div className="mt-4 space-y-3">
                          {(progressDebug.recent_progress || []).length ? (
                            progressDebug.recent_progress.map((entry) => (
                              <div key={entry.user_id} className="rounded-2xl border border-[#2a2a32] bg-[#111117] px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium text-white">{entry.name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-400">{entry.email || entry.user_id}</p>
                                  </div>
                                  <span className="rounded-full border border-aqua/30 bg-aqua/10 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-aqua">
                                    {entry.tier || 'unknown'}
                                  </span>
                                </div>
                                <p className="mt-3 text-xs text-gray-300">
                                  Steps: {(entry.onboarding_steps_completed || []).length
                                    ? entry.onboarding_steps_completed.map(formatDebugLabel).join(', ')
                                    : 'None yet'}
                                </p>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                                  <span>Updated {formatDebugDateTime(entry.updated_at)}</span>
                                  <span>Completed {formatDebugDateTime(entry.onboarding_completed_at)}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-400">No progress records yet.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-[#2a2a32] bg-[#16161d] p-4">
                        <div>
                          <h4 className="text-sm font-semibold text-white">Recent tracked events</h4>
                          <p className="mt-1 text-xs text-gray-400">Latest analytics writes for the first event layer.</p>
                        </div>

                        <div className="mt-4 space-y-3">
                          {(progressDebug.recent_events || []).length ? (
                            progressDebug.recent_events.map((entry) => (
                              <div key={entry.event_id} className="rounded-2xl border border-[#2a2a32] bg-[#111117] px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium text-white">{formatDebugLabel(entry.event_type)}</p>
                                    <p className="text-xs text-gray-400">{entry.name || 'Unknown'} • {entry.email || entry.user_id}</p>
                                  </div>
                                  <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-cyan-200">
                                    {entry.surface || 'unknown'}
                                  </span>
                                </div>
                                <p className="mt-3 text-[11px] text-gray-500">Occurred {formatDebugDateTime(entry.occurred_at)}</p>
                                {Object.keys(entry.metadata || {}).length ? (
                                  <pre className="mt-3 overflow-x-auto rounded-xl border border-[#22222b] bg-[#0d0d12] px-3 py-2 text-[11px] text-gray-300">
                                    {JSON.stringify(entry.metadata, null, 2)}
                                  </pre>
                                ) : null}
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-400">No product events tracked yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {analytics?.tier_breakdown && (
        <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-aqua" />
            Member Tier Breakdown
          </h3>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {Object.entries(analytics.tier_breakdown).map(([tier, count]) => (
              <div key={tier} className="p-4 bg-[#16161a] rounded-xl text-center">
                <p className="text-gray-400 text-sm capitalize">{tier.replace('_', ' ')}</p>
                <span className="text-xl font-bold text-white">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {atRiskMembers.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/20 p-6">
          <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Churn Risk Alert
          </h3>
          <p className="text-gray-400 text-sm mb-4">Members who may need outreach to prevent churn:</p>
          <div className="space-y-3">
            {atRiskMembers.map((member, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-[#16161a] rounded-xl">
                <div>
                  <p className="text-white font-medium">{member.name}</p>
                  <p className="text-gray-500 text-sm">Last active: {member.last_active === 'Never' ? 'Never' : `${member.days_inactive} days ago`}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={member.risk_level === 'High' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}>
                    {member.risk_level} Risk
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsTab;
